import { useTranslation } from 'react-i18next';

import StopFill from '@/assets/icons/ri_stop_fill.svg?component';
import { Badge, type BadgeTone } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { Caption } from '@/components/atoms/Eyebrow';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';

import { formatWinchMeterChip } from '../../helpers/formatWinchValue';

import styles from './WinchInfoBar.module.scss';

interface WinchInfoBarProps {
    bioscopeId: number | undefined;
    winchId: number | undefined;
    distance: number | null;
    depth: number | null;
    online: boolean;
    moving: boolean;
    calibrated: boolean;
    showStop: boolean;
    stopTooltip: string;
    onStop: () => void;
}

function IdChip(props: { label: string; value: string }) {
    const { label, value } = props;
    return (
        <span className={styles.idPill}>
            <Caption>{label}</Caption>
            <span className={styles.idDivider} aria-hidden="true" />
            <span className={styles.idValue}>{value}</span>
        </span>
    );
}

export function WinchInfoBar(props: WinchInfoBarProps) {
    const {
        bioscopeId,
        winchId,
        distance,
        depth,
        online,
        moving,
        calibrated,
        showStop,
        stopTooltip,
        onStop,
    } = props;
    const { t } = useTranslation();

    const motionText = !online
        ? t('winchControl.status.offline')
        : moving
          ? t('winchControl.status.moving')
          : t('winchControl.status.idle');
    const motionTone: BadgeTone = !online ? 'neutral' : moving ? 'success' : 'warning';
    const calibrationText = calibrated
        ? t('winchControl.status.calibrated')
        : t('winchControl.status.notCalibrated');
    const unit = t('winchControl.position.meters');
    const motionTooltip = !online
        ? t('winchControl.tooltip.statusOffline')
        : moving
          ? t('winchControl.tooltip.statusMoving')
          : t('winchControl.tooltip.statusIdle');
    const calibrationTooltip = calibrated
        ? t('winchControl.tooltip.statusCalibrated')
        : t('winchControl.tooltip.statusNotCalibrated');

    return (
        <div className={styles.bar}>
            <IdChip
                label={t('winchControl.info.bioscopeId')}
                value={bioscopeId?.toString() || '-'}
            />
            <IdChip label={t('winchControl.info.winchId')} value={winchId?.toString() || '-'} />
            <span className={styles.divider} aria-hidden="true" />
            <span className={styles.statusLabel}>{t('winchControl.info.winchStatus')}</span>
            <Tooltip content={motionTooltip} placement="bottom" layout="fit">
                <Badge size="lg" tone={motionTone} className={styles.hasTooltip}>
                    {motionText}
                </Badge>
            </Tooltip>
            <Tooltip content={calibrationTooltip} placement="bottom" layout="fit">
                <Badge
                    size="lg"
                    tone={calibrated ? 'success' : 'elevated'}
                    className={styles.hasTooltip}>
                    {calibrationText}
                </Badge>
            </Tooltip>
            <span className={styles.divider} aria-hidden="true" />
            <IdChip
                label={t('winchControl.card.distance')}
                value={formatWinchMeterChip(distance, unit)}
            />
            <IdChip
                label={t('winchControl.card.depth')}
                value={formatWinchMeterChip(depth, unit)}
            />
            {showStop && (
                <>
                    <span className={styles.divider} aria-hidden="true" />
                    <Button
                        className={styles.stop}
                        variant="dangerSolid"
                        onClick={onStop}
                        trackingId="winch_info_stop"
                        tooltip={stopTooltip}
                        icon={
                            <SvgIcon
                                icon={StopFill}
                                size="small"
                                svgProps={{ 'aria-hidden': true }}
                            />
                        }
                        tooltipPlacement="left">
                        {t('winchControl.button.stop')}
                    </Button>
                </>
            )}
        </div>
    );
}
